import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import SettingSection from "@/modules/shared/SettingSection"
import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import { Loader } from "@/UI"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { useDriverSettings } from "@/hooks/useDriverSettings"
import DriverPayForm from "./DriverPayForm/DriverPayForm.container"
import DriverPayCard from "./DriverPayCard/DriverPayCard.container"
import DriverPayFormModal from "./DriverPayFormModal/DriverPayFormModal.container"
import { useTeams } from "@/hooks/useTeams"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useClients } from "@/hooks/useClients"
import { useFlags } from "flagsmith/react"
import DriverPayIncentiveModal from "./DriverPayIncentiveModal/DriverPayIncentiveModal"

interface DriverPaySettingsProps {
  type?: "clients" | "teams"
}

const DriverPaySettings: React.FC<DriverPaySettingsProps> = ({ type = "teams" }) => {
  const { t } = useTranslation()

  const { org } = useRecoilValue(userAtom)
  const flags = useFlags(["nash_edg_demo"])
  const { driverSettings, fetchDriverSettings, isLoading: settingsLoading } = useDriverSettings()
  const { teams, isLoading: teamsLoading } = useTeams()
  const { clients, clientPay, fetchClientPay, isLoading: clientsLoading } = useClients()

  const isLoading = settingsLoading || teamsLoading || clientsLoading

  useEffect(() => {
    if (type === "teams") {
      fetchDriverSettings()
    } else {
      fetchClientPay()
    }
  }, [org?.id, type])

  const showTeamPricings = teams.length > 0 && driverSettings?.driverPay?.length
  const showClientPricings = clients.length > 0 && clientPay?.length

  return (
    <SettingSection
      title={type === "teams" ? t("profile.common.driverPay") : t("common.clientsPricing")}
      actionButton={
        <div className="flex gap-x-2">
          {flags.nash_edg_demo.enabled && (
            <DriverPayIncentiveModal
              button={
                <KosmoButton type="primary">
                  <div className="flex items-center gap-x-2">
                    <FontAwesomeIcon icon={faPlus} color="#FFF" />
                    <p>{t("profile.DriverPaySettings.createIncentive")}</p>
                  </div>
                </KosmoButton>
              }
            />
          )}
          {(!!showTeamPricings || !!showClientPricings) && (
            <DriverPayFormModal
              button={
                <KosmoButton type="primary">
                  <div className="flex items-center gap-x-2">
                    <FontAwesomeIcon icon={faPlus} color="#FFF" />
                    <p>{t("profile.DriverPaySettings.createPayRule")}</p>
                  </div>
                </KosmoButton>
              }
              type={type}
            />
          )}
        </div>
      }
    >
      <SettingContentWrapper>
        {isLoading ? (
          <Loader fontSize={40} size="small" />
        ) : (
          <>
            {type === "teams" &&
              (showTeamPricings ? (
                <div className="flex flex-col space-y-4">
                  {driverSettings?.driverPay?.map((config) => (
                    <DriverPayCard config={config} />
                  ))}
                </div>
              ) : (
                <DriverPayForm config={driverSettings?.driverPay?.[0]} />
              ))}
            {type === "clients" &&
              (showClientPricings ? (
                <div className="flex flex-col space-y-4">
                  {clientPay?.map((config) => (
                    <DriverPayCard config={config} type="clients" />
                  ))}
                </div>
              ) : (
                <DriverPayForm config={clientPay?.[0]} type="clients" />
              ))}
          </>
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default DriverPaySettings
